import { getProductItemPriceInfo } from '@croquiscom/zigzag-server-common/lib/order_utils';

import partition from 'lodash-es/partition';
import { useCallback } from 'react';
import { CouponDiscountInfo, ShippingType, CouponTargetType } from 'api2/types';
import { CartShopList } from 'components/cart/store';
import { getOptionNames } from 'components/cart/util';
import { OrderShopList } from 'components/order-sheets/store/order-shop-list';
import LocalDataStorage from 'util/coupon-utils/LocalDataStorage';
import { CouponPayload, NomalizedItem, NormalizedItem } from 'util/coupon-utils/types';
import { getZigzinShopId } from 'util/zigzin';

export { LocalDataStorage };

export const coupon_order_array = [
  CouponTargetType.ORDER_ITEM,
  CouponTargetType.SHOP,
  CouponTargetType.ORDER,
  CouponTargetType.SHIPPING_FEE,
  CouponTargetType.POINT,
];

export const createPayload = (payload: CouponPayload): CouponPayload => {
  return Object.assign(
    {
      shop_id_list: [],
      product_item_id_list: [],
      actual_discount_amount: 0,
    },
    payload,
  );
};

export const normalizeCartItemList = (
  cart_shop_list: CartShopList,
  selected_item_id_list: { [product_item_id: string]: boolean },
  orderable_item_id_list: { [product_item_id: string]: boolean },
): NormalizedItem[] => {
  return cart_shop_list.reduce<NormalizedItem[]>((acc, { shop, shipping_group_list }) => {
    const [zigzin_item_list, normal_item_list] = partition(
      shipping_group_list,
      (group) => group.shipping_info.shipping_type === ShippingType.ZIGZIN,
    ).map((group_list, i) => {
      const is_zigzin = i === 0;

      return group_list
        .flatMap(({ cart_item_list }) => cart_item_list)
        .filter(({ product_item_id, cart_item_hash }) => selected_item_id_list[`${product_item_id}${cart_item_hash}`])
        .map<NormalizedItem>((cart_item) => {
          const origin_id = cart_item.origin_shop?.id ?? shop.id;
          return {
            shop: {
              id: is_zigzin ? getZigzinShopId() : shop.id,
              img: shop.typical_image_url ?? '',
              name: shop.name,
              origin_id,
            },
            order_available: orderable_item_id_list[`${cart_item.product_item_id}${cart_item.cart_item_hash}`],
            product_id: cart_item.product_item.product.id,
            product_item_id: cart_item.product_item_id,
            product_name: cart_item.product_item.product.name,
            product_img: cart_item.product_item.product.image.url || '',
            option_names: getOptionNames(cart_item),
            price: getProductItemPriceInfo(cart_item.product_item, cart_item.additional_option_detail_list || []).price,
            quantity: cart_item.quantity,
            selected: selected_item_id_list[`${cart_item.product_item_id}${cart_item.cart_item_hash}`],
          };
        });
    });
    return acc.concat(zigzin_item_list).concat(normal_item_list);
  }, []);
};

export const normalizeOrderItemList = (order_shop_list: OrderShopList): NormalizedItem[] => {
  return order_shop_list.reduce<NormalizedItem[]>((acc, { shop, shipping_group_list }) => {
    const [zigzin_item_list, normal_item_list] = partition(
      shipping_group_list,
      (group) => group.shipping_info.shipping_type === ShippingType.ZIGZIN,
    ).map((group_list, i) => {
      const is_zigzin = i === 0;
      return group_list
        .flatMap(({ order_item_list }) => order_item_list)
        .map<NormalizedItem>((order_item) => {
          const origin_id = order_item.origin_shop?.id ?? shop.id;
          return {
            shop: {
              id: is_zigzin ? getZigzinShopId() : shop.id,
              img: shop.typical_image_url ?? null,
              name: shop.name,
              origin_id,
            },
            order_available: true,
            product_id: order_item.product_info.id,
            product_item_id: order_item.product_item_id,
            product_name: order_item.product_info.name,
            product_img: order_item.product_info.image_url,
            option_names: order_item.product_info.options,
            price: order_item.product_info.price,
            quantity: order_item.quantity,
            selected: true,
          };
        });
    });
    return acc.concat(zigzin_item_list).concat(normal_item_list);
  }, []);
};

export const byItemBasicCriteria = (item1: NomalizedItem, item2: NomalizedItem) => {
  const item1TotalAmount = item1.price * item1.quantity;
  const item2TotalAmount = item2.price * item2.quantity;
  return item2TotalAmount - item1TotalAmount;
};

export const byCouponBasicCriteria = (coupon1: CouponDiscountInfo, coupon2: CouponDiscountInfo) => {
  if (coupon2.calculated_discount_amount !== coupon1.calculated_discount_amount) {
    // 할인 금액 큰 순서대로
    return coupon2.calculated_discount_amount - coupon1.calculated_discount_amount;
  } else {
    // 할인 금액 같은 경우
    if (Number(coupon1.date_expire) >= Number(coupon2.date_expire)) {
      // 사용 마감 빠른순
      return 1;
    } else {
      return -1;
    }
  }
};

export const checkBpCoupon =
  (discount_rate_bp: number | null | undefined, max_discount_amount: number | null | undefined) =>
  <TT, FT>(truthy_text: TT, falsy_text: FT) => {
    const is_bp_coupon = discount_rate_bp && max_discount_amount;
    return is_bp_coupon ? truthy_text : falsy_text;
  };

export const useCouponDescription = () => {
  return useCallback(
    (
      discount_rate_bp: number | null | undefined,
      max_discount_amount: number | null | undefined,
      min_required_amount?: number | null,
    ) => {
      const conditionalDesc = checkBpCoupon(discount_rate_bp, max_discount_amount);
      const min_required_amount_locale = min_required_amount ? min_required_amount.toLocaleString('ko-KR') : 0;
      const max_discount_amount_locale = max_discount_amount ? max_discount_amount.toLocaleString('ko-KR') : 0;

      const bp_desc = `${min_required_amount_locale}원 이상 구매시 최대 ${max_discount_amount_locale}원 할인`;
      const basic_desc = `${min_required_amount_locale}원 이상 구매 시 사용 가능`;
      return conditionalDesc(bp_desc, basic_desc);
    },
    [],
  );
};
